import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CreditCardIcon, DollarSignIcon, TrendingUpIcon, Loader2 } from 'lucide-react';
import { useOrganizationSubscription } from '@/hooks/useOrganizationSubscription';
import { useOrganizationSelection } from '@/hooks/useOrganizationSelection';
import { formatPrice } from '@envase/api/client';
import { Loader } from '@/components/ui/loader';

export const Route = createFileRoute('/_authenticated/dashboard/billing')({
  component: BillingPage,
});

function BillingPage() {
  const navigate = useNavigate();
  const { selectedOrganization } = useOrganizationSelection();
  const orgId = selectedOrganization?.id;
  const { data: subscriptionData, isLoading: isLoadingSubscription } = useOrganizationSubscription(orgId);

  const handleChangePlan = () => {
    if (orgId) {
      navigate({ to: `/org/${orgId}/plans` });
    }
  };

  // Calculate next billing date
  const nextBillingDate = subscriptionData?.subscription.currentPeriodEnd
    ? new Date(subscriptionData.subscription.currentPeriodEnd)
    : null;

  // Calculate days until next billing
  const daysUntilBilling = nextBillingDate
    ? Math.ceil((nextBillingDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Billing</h1>
        <p className="text-gray-600 mt-2">Manage your subscription and billing</p>
      </div>

      {isLoadingSubscription ? (
        <div className="flex items-center justify-center py-12">
          <Loader variant="spinner" size="lg" color="primary" text="Loading billing information..." />
        </div>
      ) : subscriptionData ? (
        <>
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Current Plan</CardTitle>
                <CreditCardIcon className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{subscriptionData.plan.name}</div>
                <p className="text-xs text-muted-foreground">
                  {formatPrice(subscriptionData.plan.price)}/{subscriptionData.plan.interval === 'month' ? 'month' : 'year'}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Next Billing</CardTitle>
                <DollarSignIcon className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatPrice(subscriptionData.plan.price)}</div>
                <p className="text-xs text-muted-foreground">
                  {nextBillingDate ? `Due on ${formatDate(nextBillingDate)}` : 'No billing date'}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Status</CardTitle>
                <TrendingUpIcon className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <Badge className={`${
                  subscriptionData.subscription.status === 'active' 
                    ? 'bg-green-500' 
                    : subscriptionData.subscription.status === 'past_due'
                    ? 'bg-yellow-500'
                    : 'bg-red-500'
                } text-white`}>
                  {subscriptionData.subscription.status}
                </Badge>
                {daysUntilBilling !== null && (
                  <p className="text-xs text-muted-foreground mt-2">
                    {daysUntilBilling > 0 ? `${daysUntilBilling} days remaining` : 'Expired'}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Subscription Details</CardTitle>
              <CardDescription>Your current plan and features</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium">{subscriptionData.plan.name}</h3>
                  <p className="text-sm text-gray-500">{subscriptionData.plan.description || 'Current subscription plan'}</p>
                  {subscriptionData.subscription.cancelAtPeriodEnd && (
                    <p className="text-sm text-orange-600 mt-1">
                      Will cancel on {formatDate(new Date(subscriptionData.subscription.currentPeriodEnd))}
                    </p>
                  )}
                </div>
                <Badge className={
                  subscriptionData.subscription.status === 'active' 
                    ? 'bg-green-500' 
                    : subscriptionData.subscription.status === 'past_due'
                    ? 'bg-yellow-500'
                    : 'bg-red-500'
                }>
                  {subscriptionData.subscription.status}
                </Badge>
              </div>
              <div className="pt-4 border-t">
                <Button variant="outline" onClick={handleChangePlan}>Change Plan</Button>
                {subscriptionData.subscription.status === 'active' && !subscriptionData.subscription.cancelAtPeriodEnd && (
                  <Button variant="ghost" className="ml-2" disabled>
                    Cancel Subscription
                  </Button>
                )}
                {subscriptionData.subscription.cancelAtPeriodEnd && (
                  <Button variant="ghost" className="ml-2" disabled>
                    Cancelling
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Billing History</CardTitle>
              <CardDescription>Past invoices and payments</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="text-center py-8 text-gray-500 text-sm">
                  Billing history will be available soon
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500 mb-4">No subscription found</p>
            {orgId && (
              <Button onClick={handleChangePlan}>View Plans</Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

